// src/components/LandingPage.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, KeyRound, Plus, Search, Sparkles, TrendingUp } from 'lucide-react';
import HeaderBar from './HeaderBar';
import SurveyList from './SurveyList';
import { apiGet, bootstrapBffSession } from '../lib/apiClient';
import { rememberAccessCode } from '../lib/accessCodeStorage';
import Footer from './Footer';
import '../styles/LandingPage.css';

const quickFilters = [
  { metric: 'responses', label: 'Top Responses' },
  { metric: 'likes', label: 'Top Likes' },
  { metric: 'discussion', label: 'Top Discussion' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [searchMode, setSearchMode] = useState('public');
  const inputRef = useRef(null);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [lastSearchKind, setLastSearchKind] = useState(null);
  const [lastSearchTerm, setLastSearchTerm] = useState('');
  const [sugs, setSugs] = useState([]);
  const [showSugs, setShowSugs] = useState(false);
  const [keywords, setKeywords] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [activeQuickFilter, setActiveQuickFilter] = useState('');
  const skipNextSuggestions = useRef(false);

  const hasResultsView = results !== null;

  useEffect(() => {
    const clearHandler = () => {
      setResults(null);
      setQuery('');
      setShowSugs(false);
      setSugs([]);
      setLastSearchKind(null);
      setLastSearchTerm('');
      setActiveQuickFilter('');
    };
    window.addEventListener('user:signedout', clearHandler);
    return () => window.removeEventListener('user:signedout', clearHandler);
  }, []);

  const handlePublicSubmit = async (event) => {
    if (event) event.preventDefault();
    const q = (query || '').trim();
    if (!q) {
      setResults(null);
      setActiveQuickFilter('');
      return;
    }
    setLastSearchKind('public');
    setLastSearchTerm(q);
    setActiveQuickFilter('');
    setSearching(true);
    try {
      await bootstrapBffSession().catch(() => {});
      const params = new URLSearchParams({ q }).toString();
      const res = await apiGet(`/api/surveys/search?${params}`);
      setResults(Array.isArray(res?.items) ? res.items : []);
    } catch (_) {
      setResults([]);
    } finally {
      setSearching(false);
      setShowSugs(false);
    }
  };

  const handleCodeSearch = async (event) => {
    if (event) event.preventDefault();
    const code = (query || '').trim();
    if (!code) {
      setResults(null);
      setActiveQuickFilter('');
      return;
    }
    setLastSearchKind('code');
    setLastSearchTerm(code);
    setActiveQuickFilter('');
    setSearching(true);
    try {
      await bootstrapBffSession().catch(() => {});
      const params = new URLSearchParams({ code }).toString();
      const res = await apiGet(`/api/surveys/search?${params}`);
      const items = Array.isArray(res?.items) ? res.items : [];
      setResults(items);
      items.forEach((item) => {
        const surveyId = item?.surveyId ?? item?.id ?? item?.survey_id ?? item?.surveyid;
        if (surveyId) rememberAccessCode(surveyId, code);
      });
    } catch (_) {
      setResults([]);
    } finally {
      setSearching(false);
      setShowSugs(false);
    }
  };

  const handleSearchSubmit = (event) => {
    if (searchMode === 'code') return handleCodeSearch(event);
    return handlePublicSubmit(event);
  };

  const runQuickFilter = async ({ metric, label }) => {
    setSearching(true);
    setActiveQuickFilter(metric);
    setLastSearchKind('public');
    setLastSearchTerm(label);
    try {
      const params = new URLSearchParams({ metric, limit: '20' }).toString();
      const res = await apiGet(`/api/surveys/top?${params}`);
      setResults(Array.isArray(res?.items) ? res.items : []);
    } catch (_) {
      setResults([]);
    } finally {
      setSearching(false);
      setShowSugs(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await apiGet('/api/surveys/keywords?limit=20');
        setKeywords(Array.isArray(res?.keywords) ? res.keywords : []);
      } catch (_) {
        setKeywords([]);
      }
    })();
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setFeaturedLoading(true);
      try {
        const params = new URLSearchParams({ metric: 'responses', limit: '6' }).toString();
        const res = await apiGet(`/api/surveys/top?${params}`);
        if (alive) setFeatured(Array.isArray(res?.items) ? res.items : []);
      } catch (_) {
        if (alive) setFeatured([]);
      } finally {
        if (alive) setFeaturedLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (searchMode !== 'public') {
      setShowSugs(false);
      setSugs([]);
      return undefined;
    }
    if (skipNextSuggestions.current) {
      skipNextSuggestions.current = false;
      setShowSugs(false);
      setSugs([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      const q = (query || '').trim();
      if (q.length < 3) {
        setSugs([]);
        setShowSugs(false);
        return;
      }
      try {
        const res = await apiGet(`/api/surveys/suggest?${new URLSearchParams({ q, limit: '6' }).toString()}`);
        const baseRaw = Array.isArray(res?.suggestions) ? res.suggestions : [];
        const seenSurvey = new Set();
        const base = [];
        for (const s of baseRaw) {
          const key = String(s.title || '').toLowerCase();
          if (key && !seenSurvey.has(key)) {
            seenSurvey.add(key);
            base.push(s);
          }
        }

        const surveyTitlesLower = base.map((s) => String(s.title || '').toLowerCase());
        const kwMatches = (keywords || [])
          .filter((k) => (k.keyword || '').startsWith(q.toLowerCase()))
          .filter((k) => {
            const kw = String(k.keyword || '').toLowerCase();
            if (!kw) return false;
            if (seenSurvey.has(kw)) return false;
            return !surveyTitlesLower.some((t) => t.startsWith(kw) || t.includes(kw) || kw.startsWith(t));
          })
          .map((k) => ({ type: 'keyword', keyword: k.keyword, count: k.count }));

        setSugs([...base, ...kwMatches]);
        setShowSugs(true);
      } catch (_) {
        setSugs([]);
        setShowSugs(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, keywords, searchMode]);

  const onSelectSuggestion = async (sug) => {
    if (!sug) return;
    skipNextSuggestions.current = true;
    setShowSugs(false);
    setSugs([]);
    const term = (sug.type === 'survey' ? sug.title : sug.keyword || '').trim();
    if (!term) return;
    setQuery(term);
    setLastSearchKind('public');
    setLastSearchTerm(term);
    setActiveQuickFilter('');
    setSearching(true);
    try {
      await bootstrapBffSession().catch(() => {});
      const params = new URLSearchParams({ q: term }).toString();
      const res = await apiGet(`/api/surveys/search?${params}`);
      setResults(Array.isArray(res?.items) ? res.items : []);
    } catch (_) {
      setResults([]);
    } finally {
      setSearching(false);
      setShowSugs(false);
    }
  };

  useEffect(() => {
    const state = location.state || {};
    const paramsObj = new URLSearchParams(location.search);
    const createdId = state.createdSurveyId || paramsObj.get('s');
    const shareToken = paramsObj.get('share_token');
    if (!createdId) return;
    (async () => {
      try {
        const queryParams = new URLSearchParams({ survey_id: createdId });
        if (shareToken) queryParams.set('share_token', shareToken);
        const res = await apiGet(`/api/surveys/search-by-id?${queryParams.toString()}`);
        setResults(Array.isArray(res?.items) ? res.items : []);
        setLastSearchKind('public');
        setLastSearchTerm('New survey');
      } catch (_) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const searchPlaceholder = searchMode === 'code'
    ? 'Enter an access code'
    : 'Search public feedback surveys by topic, product, team, or community';
  const searchAriaLabel = searchMode === 'code'
    ? 'Enter an access code to find private surveys'
    : 'Search public surveys';

  return (
    <div className={`landing-page landing-page--revamp ${hasResultsView ? 'landing-page--results' : ''}`}>
      <HeaderBar isSurveyLab />

      <main className="landing-shell">
        <section className="landing-hero" aria-label="getSurvey home">
          <div className="landing-hero__copy">
            <div className="landing-eyebrow">
              <Sparkles size={16} aria-hidden="true" />
              Anonymous feedback campaigns
            </div>
            <p>
              Create AI-assisted surveys, share them with a link or access code, and turn responses into simple insights.
            </p>
            <div className="landing-hero__actions">
              <button
                type="button"
                className="landing-btn landing-btn--primary"
                onClick={() => navigate('/create-survey')}
              >
                <Plus size={17} aria-hidden="true" />
                Create Survey
              </button>
              <a className="landing-btn landing-btn--secondary" href="#survey-results">
                Browse Surveys
                <ArrowRight size={17} aria-hidden="true" />
              </a>
            </div>
          </div>

          <form className="landing-search-card" onSubmit={handleSearchSubmit} role="search" aria-label="Search surveys">
            <div className="search-mode-tabs" role="tablist" aria-label="Search mode">
              <button
                type="button"
                className={`search-mode-tab ${searchMode === 'public' ? 'search-mode-tab--active' : ''}`}
                onClick={() => { setSearchMode('public'); setShowSugs(false); inputRef.current?.focus(); }}
                role="tab"
                aria-selected={searchMode === 'public'}
              >
                <Search size={15} aria-hidden="true" />
                Public Surveys
              </button>
              <button
                type="button"
                className={`search-mode-tab ${searchMode === 'code' ? 'search-mode-tab--active' : ''}`}
                onClick={() => { setSearchMode('code'); setShowSugs(false); inputRef.current?.focus(); }}
                role="tab"
                aria-selected={searchMode === 'code'}
              >
                <KeyRound size={15} aria-hidden="true" />
                Access Code
              </button>
            </div>

            <div className="search-input-wrap landing-search-input-wrap">
              <Search className="landing-search-icon" size={18} aria-hidden="true" />
              <input
                ref={inputRef}
                className="search-input landing-search-input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchAriaLabel}
                autoFocus
              />
              {showSugs && sugs.length > 0 && (
                <ul className="suggestions landing-suggestions" role="listbox" aria-label="Search suggestions">
                  {sugs.map((s, i) => (
                    <li
                      key={i}
                      className="suggestion-item landing-suggestion-item"
                      role="option"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onSelectSuggestion(s)}
                    >
                      <span>{s.type === 'survey' ? s.title : s.label || s.keyword}</span>
                      {s.type === 'survey' && (
                        <span className="landing-suggestion-meta">
                          {s.responses_count ? `${s.responses_count} responses` : (s.like_count ? `${s.like_count} likes` : '')}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="search-actions landing-search-actions">
              <button className="landing-btn landing-btn--primary" type="submit" disabled={searching}>
                {searching ? 'Searching...' : searchMode === 'code' ? 'Find by Code' : 'Search Public'}
              </button>
              <button
                className="landing-btn landing-btn--secondary"
                type="button"
                onClick={() => navigate('/create-survey', { state: { draftContent: query.trim() } })}
              >
                Create from Topic
              </button>
            </div>

            <div className="quick-filter-row" aria-label="Quick survey filters">
              {quickFilters.map((filter) => (
                <button
                  key={filter.metric}
                  type="button"
                  className={`quick-filter-chip ${activeQuickFilter === filter.metric ? 'quick-filter-chip--active' : ''}`}
                  onClick={() => runQuickFilter(filter)}
                >
                  <TrendingUp size={14} aria-hidden="true" />
                  {filter.label}
                </button>
              ))}
            </div>
          </form>
        </section>

        <section id="survey-results" className="landing-content-panel" aria-live="polite">
          {results !== null ? (
            <>
              <div className="landing-section-header">
                <div>
                  <h2>{lastSearchKind === 'code' ? 'Access Code Results' : 'Survey Results'}</h2>
                  {lastSearchTerm && <p>{lastSearchKind === 'code' ? 'Private search' : 'Showing'}: {lastSearchTerm}</p>}
                </div>
                <button
                  type="button"
                  className="landing-link-button"
                  onClick={() => {
                    setResults(null);
                    setLastSearchKind(null);
                    setLastSearchTerm('');
                    setActiveQuickFilter('');
                  }}
                >
                  Clear
                </button>
              </div>

              {searching && <div className="landing-state-card">Searching surveys...</div>}

              {!searching && Array.isArray(results) && results.length > 0 && (
                <SurveyList itemsOverride={results} intentQuery="" />
              )}

              {!searching && Array.isArray(results) && results.length === 0 && (
                <div className="landing-empty-state">
                  <h3>No surveys found{lastSearchTerm ? ` for "${lastSearchTerm}"` : ''}.</h3>
                  <p>
                    {lastSearchKind === 'code'
                      ? 'Check the access code and try again.'
                      : 'Start a new survey from this topic or browse the most active surveys.'}
                  </p>
                  <div className="landing-empty-state__actions">
                    {lastSearchKind !== 'code' && (
                      <button
                        className="landing-btn landing-btn--primary"
                        onClick={() => navigate('/create-survey', { state: { draftContent: lastSearchTerm } })}
                        type="button"
                      >
                        Create Survey from Topic
                      </button>
                    )}
                    <button
                      className="landing-btn landing-btn--secondary"
                      onClick={() => runQuickFilter(quickFilters[0])}
                      type="button"
                    >
                      Browse Top Responses
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="landing-section-header">
                <div>
                  <h2>Featured Surveys</h2>
                  <p>Explore active feedback campaigns or use the filters above.</p>
                </div>
              </div>
              {featuredLoading && <div className="landing-state-card">Loading featured surveys...</div>}
              {!featuredLoading && featured.length > 0 && (
                <SurveyList itemsOverride={featured} intentQuery="" />
              )}
              {!featuredLoading && featured.length === 0 && (
                <div className="landing-empty-state">
                  <h3>No featured surveys yet.</h3>
                  <p>Create the first survey or search for a specific topic.</p>
                  <button className="landing-btn landing-btn--primary" onClick={() => navigate('/create-survey')} type="button">
                    Create Survey
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        <div className="gpt-cta">
          <span className="gpt-cta__label">Want AI help drafting surveys?</span>
          <a
            className="gpt-cta__link"
            href="https://chatgpt.com/g/g-68f49b049b3c819194615b10927499f4-getsurvey"
            target="_blank"
            rel="noreferrer noopener"
          >
            Try this app on ChatGPT
          </a>
        </div>
      </main>

      <Footer betaOnNewLine />
    </div>
  );
}
